import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
} from "@mui/material";
import PageWrapper from "./PageWrapper";
import { useQuizContext } from "./QuizContext";
import ProgressBar from "../../components/ProgressBar/ProgressBar";
import "./SignupCommon.css";
import AlertIcon from "../../assets/images/alert.png";

const SignupPage11: React.FC = () => {
  const [feet, setFeet] = useState<string | null>(null);
  const [inches, setInches] = useState<string | null>(null);
  const [height, setHeight] = useState<number>(0);
  const [weight, setWeight] = useState<string>("");
  const [bmi, setBmi] = useState<number | 0>(0);
  const [error, setError] = useState<string>("");
  const [isBmiValid, setIsBmiValid] = useState(true);
  const [minValidBmi, setMinValidBmi] = useState(27);

  const { setAnswer } = useQuizContext();
  const navigate = useNavigate();

  useEffect(() => {
    const step6Completed = localStorage.getItem("step-6-completed");
    if (step6Completed) {
      navigate("/getting-started/step-7", { replace: true });
    } else {
      navigate("/getting-started/step-6", { replace: true });
    }
  }, []);

  useEffect(() => {
    const storedHeight = localStorage.getItem("height");
    const storedWeight = localStorage.getItem("weight");

    if (storedWeight) {
      try {
        const parsedWeight = JSON.parse(storedWeight);
        setWeight(parsedWeight.value || "");
      } catch (error) {
        console.error("Error parsing weight from localStorage:", error);
      }
    }

    if (storedHeight) {
      try {
        const parsedHeight = JSON.parse(storedHeight);
        const totalHeightInInches = parseInt(parsedHeight.value, 10);
        const feetValue = Math.floor(totalHeightInInches / 12);
        const inchesValue = totalHeightInInches % 12;
        setFeet(feetValue.toString());
        setInches(inchesValue.toString());
        setHeight(totalHeightInInches);
      } catch (error) {
        console.error("Error parsing height from localStorage:", error);
      }
    }
  }, []);

  useEffect(() => {
    const medData = localStorage.getItem(
      "weight_loss_medication"
    );
    if (medData) {
      try {
        const parsed = JSON.parse(medData);
        const isTakingMed = parsed.value === "Yes";
        const threshold = isTakingMed ? 22 : 25;
        setMinValidBmi(threshold);

        if (bmi > 0) {
          const roundedBmi = parseFloat(bmi.toFixed(1));
          setIsBmiValid(roundedBmi >= minValidBmi);
        }
      } catch (err) {
        console.error("Failed to parse medication data:", err);
      }
    }
  }, [bmi]);

  const calculateBmi = (weight: number, height: number) => {
    if (weight <= 0 || height <= 0) return 0;
    const weightInKg = weight * 0.453592;
    const heightInMeters = height * 0.0254;
    return Math.min(
      Math.max(weightInKg / (heightInMeters * heightInMeters), 0),
      200
    );
  };

  useEffect(() => {
    if (height > 0 && weight) {
      const parsedWeight = parseFloat(weight);
      if (parsedWeight > 0) {
        const newBmi = calculateBmi(parsedWeight, height);
        setBmi(newBmi);
        localStorage.setItem(
          "bmi",
          JSON.stringify({ key: "bmi", value: newBmi })
        );
        setError("");
      }
    }
  }, [height, weight]);


  useEffect(() => {
    if (feet && inches) {
      const feetValue = parseInt(feet, 10);
      const inchesValue = parseInt(inches, 10);
      const totalHeightInInches = feetValue * 12 + inchesValue;
      setHeight(totalHeightInInches);
      localStorage.setItem(
        "height",
        JSON.stringify({ key: "height", value: `${totalHeightInInches}` })
      );
      if (weight) {
        const parsedWeight = parseFloat(weight);
        const newBmi = calculateBmi(parsedWeight, totalHeightInInches);
        setBmi(newBmi);
        localStorage.setItem(
          "bmi",
          JSON.stringify({ key: "bmi", value: newBmi })
        );
      }
    }
  }, [feet, inches]);

  const handleHeightChange = () => {
    if (feet && inches) {
      const feetValue = parseInt(feet, 10);
      const inchesValue = parseInt(inches, 10);
      const totalHeightInInches = feetValue * 12 + inchesValue;
      setHeight(totalHeightInInches);
      setAnswer("height", `${totalHeightInInches}`);
      localStorage.setItem(
        "height",
        JSON.stringify({ key: "height", value: `${totalHeightInInches}` })
      );
      if (weight) {
        const parsedWeight = parseFloat(weight);
        const newBmi = calculateBmi(parsedWeight, totalHeightInInches);
        setBmi(newBmi);
        localStorage.setItem(
          "bmi",
          JSON.stringify({ key: "bmi", value: newBmi })
        );
      }
    }
  };

  const handleWeightChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newWeight = e.target.value;
    setWeight(newWeight);

    if (!newWeight || parseFloat(newWeight) <= 0) {
      setError("Weight should be greater than 0.");
      setBmi(0);
      return;
    }

    setError("");
    localStorage.setItem(
      "weight",
      JSON.stringify({ key: "weight", value: newWeight })
    );

    if (height > 0) {
      const parsedWeight = parseFloat(newWeight);
      const newBmi = calculateBmi(parsedWeight, height);
      setBmi(newBmi);
      localStorage.setItem(
        "bmi",
        JSON.stringify({ key: "bmi", value: newBmi })
      );
    }
  };

  const handleNext = () => {
    if (feet && inches) {
      const feetValue = parseInt(feet, 10);
      const inchesValue = parseInt(inches, 10);
      const totalHeightInInches = feetValue * 12 + inchesValue;
      setHeight(totalHeightInInches);
      setAnswer("height", `${totalHeightInInches}`);
      localStorage.setItem(
        "height",
        JSON.stringify({ key: "height", value: `${totalHeightInInches}` })
      );
    }

    const roundedBmi = parseFloat(bmi.toFixed(1));
    if (roundedBmi !== null && roundedBmi >= minValidBmi) {
      setIsBmiValid(roundedBmi >= minValidBmi);
      navigate("/getting-started/step-8");
    } else {
      setIsBmiValid(false);
      navigate("/getting-started/invalid-age");
    }

    localStorage.setItem("step-7-completed", "true");
  };

  const feetOptions = Array.from({ length: 8 }, (_, i) => ({
    value: (i + 1).toString(),
    label: `${i + 1} ft`,
  }));

  const inchOptions = Array.from({ length: 12 }, (_, i) => ({
    value: i.toString(),
    label: `${i} in`,
  }));

  const isVariant = localStorage.getItem("isVariant") === "true";

  return (
    <PageWrapper>
      <div className="signup-page-container container">
        <ProgressBar progress={isVariant ? 80 : 70} step={7} />
        <div className="signup-content">
          <p className="signup-intro" style={{ marginBottom: "29px" }}>
            What is your current height & weight?
          </p>

          <div className="slider-container">
            {bmi !== null && (
              <div className="bmi-container">
                <h6 className="bmi-label">Your BMI</h6>
                <h6 className="bmi-value">{bmi.toFixed(1)}</h6>
              </div>
            )}
            <Slider
              value={bmi || 0}
              min={0}
              max={30}
              sx={{
                width: "100%",
                height: "16px",
                borderRadius: "10px",
                "& .MuiSlider-track": {
                  background:
                    "linear-gradient(90deg, #189BD7 0%, #0D5171 50%, #189BD7 100%)",
                  color: "transparent",
                },
                "& .MuiSlider-thumb": {
                  display: "none",
                },
              }}
              disabled
            />
          </div>

          {bmi > 0 && !isBmiValid && (
            <div
              className="bmi-error-container"
              style={{ display: "flex", alignItems: "center", height: "60px" }}
            >
              <img src={AlertIcon} alt="alert" />
              <p className="bmi-error-content">
                Your BMI is {bmi.toFixed(1)}. Our doctors can only prescribe if
                your BMI is {minValidBmi} or higher.
              </p>
            </div>
          )}

          <div className="dropdownAlignment">
            <FormControl fullWidth variant="outlined">
              <InputLabel id="feet-label">Feet</InputLabel>
              <Select
                labelId="feet-label"
                value={feet || ""}
                onChange={(e) => {
                  setFeet(e.target.value);
                  handleHeightChange();
                }}
                label="Feet"
                style={{ borderRadius: "123px", backgroundColor: "#fff" }}
              >
                {feetOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth variant="outlined">
              <InputLabel id="inches-label">Inches</InputLabel>
              <Select
                labelId="inches-label"
                value={inches || ""}
                onChange={(e) => {
                  setInches(e.target.value);
                  handleHeightChange();
                }}
                label="Inches"
                style={{ borderRadius: "123px", backgroundColor: "#fff" }}
              >
                {inchOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          <input
            id="weight-input"
            type="number"
            value={weight}
            onChange={handleWeightChange}
            placeholder="LBS"
            className="weight-input"
            min="0"
          />
          {error && (
            <div
              className="bmi-error-container"
              style={{
                height: "60px",
                display: "flex",
                justifyContent: "center",
                gap: "10px",
                alignItems: "center",
                marginTop: "10px",
              }}
            >
              <img src={AlertIcon} alt="alert" />
              <p className="bmi-error-content">{error}</p>
            </div>
          )}
          <button
            className="next-btn sticky-btn"
            disabled={!feet || !inches || !weight || !isBmiValid}
            onClick={handleNext}
          >
            Next →
          </button>
        </div>
      </div>
    </PageWrapper>
  );
};

export default SignupPage11;
